import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../page-action/AccessPageAction";
import { showToast } from "../../../utils/utils";
import "./../accessmanagement.scss";
import {
  ALPHABETIC_REGEX,
  showToastType
} from "../../../utils/constants/appConstants";
import { useSelector } from "react-redux";
import { buttonName, roleMange, userManage } from "../../../locales/en";
import {
  getRoleManagementDetailsURL,
  getUserManagementWithPermissionsURL,
  limitedAccessModelURL,
  navItemsMenuPermissionsURL
} from "../../../utils/constants/apiEndPoints";
import privateAxios from "../../../utils/axiosPrivate";
import {
  getActiveStatusOptions,
  getPermissionAccessOptions,
  getTwoFactorOptions
} from "../../../utils/getDropdownOptions";
import { isRadioGroupNavItem } from "../../../utils/roleUtils";
import { CREATE_ROLE, ROLE } from "../../../route/paths";

const EditRoleManage = () => {
  const { id } = useParams();
  const [roleName, setRoleName] = useState("");
  const [permissionType, setPermissionType] = useState(true);
  const [orderNumber, setOrderNumber] = useState("1");
  const [errOrderMessage, setErrOrderMessage] = useState("");
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [roleNameError, setRoleNameError] = useState("");
  const [adminMenuPermissions, setAdminMenuPermissions] = useState([]);
  const [menuArray, setMenuArray] = useState([]);
  const navigate = useNavigate();
  const initialRender = useRef(true);
  const roleNameRef = useRef(null);
  const permissionAccessRef = useRef(null);
  const orderNumberRef = useRef(null);
  const navItems = useSelector((state) => state.userManagement.navItems);
  const refsMap = {
    name: roleNameRef,
    fullAccess: permissionAccessRef,
    orderNumber: orderNumberRef
  };

  const fetchAssignedMenuPermissions = useCallback(async () => {
    try {
      const menuPermissionData = await privateAxios.get(
        navItemsMenuPermissionsURL(id)
      );
      const data = menuPermissionData.data.data;
      const menuId = data.map((checkedItems) => checkedItems.id);
      const childmenuId = data.flatMap((item) =>
        item.children ? item.children.map((child) => child.id) : []
      );
      setMenuArray([...menuId, ...childmenuId]);
    } catch {
      return [];
    }
  }, [privateAxios, id]);

  const fetchRoleDetails = async () => {
    try {
      const response = await privateAxios.get(getRoleManagementDetailsURL(id));
      const role = response.data.data;
      setRoleName(role.name);
      setPermissionType(role.fullAccess);
      setOrderNumber(role.orderNumber);
      setIsTwoFactorEnabled(role.isTwoFactorEnabled);
      setIsActive(role.isActive);

      const permissionGroupStatusInit = {};
      // Defensive: ensure role.permissions is always an array
      const rolePermissions = Array.isArray(role.permissions)
        ? role.permissions
        : [];
      const checkedPermissionsInit = rolePermissions.map(
        (permission) => permission.id
      );

      rolePermissions.forEach((permission) => {
        permissionGroupStatusInit[permission.type] = rolePermissions.every(
          (p) =>
            p.type === permission.type && checkedPermissionsInit.includes(p.id)
        );
      });
    } catch (error) {
      const msg = error?.response?.data?.message || error.message;
      showToast(msg, showToastType.error);
    }
  };

  const fetchAssignedPermissions = useCallback(
    async (id) => {
      try {
        const permissionData = await privateAxios.get(
          limitedAccessModelURL(id)
        );
        const data = permissionData.data.data;
        return data.map((permission) => permission.id);
      } catch (error) {
        const msg = error?.response?.data?.message || error.message;
        showToast(msg, showToastType.error);
        return [];
      }
    },
    [privateAxios]
  );

  useEffect(() => {
    if (navItems !== null) {
      const filterVisibleNavItems = navItems
        ?.filter((item) => item.isActive)
        .map((item) => ({
          ...item,
          children: item.children?.filter((child) => child.isActive) || []
        }));
      setAdminMenuPermissions(filterVisibleNavItems);
    }
  }, [navItems]);

  useEffect(() => {
    if (initialRender.current) {
      if (id) {
        fetchRoleDetails();
        fetchAssignedPermissions(id);
      }
      fetchAssignedMenuPermissions();

      initialRender.current = false;
    }
  }, [
    id,
    fetchAssignedPermissions,
    fetchAssignedMenuPermissions,
    menuArray.length
  ]);

  // Updates individual child selection status without affecting other selections
  const handleChildChange = (parentId, childId, checked) => {
    setMenuArray((prevMenuArray) => {
      const parent = adminMenuPermissions.find((i) => i.id === parentId);

      if (parent && isRadioGroupNavItem(parent.slugName)) {
        const otherChildIds = parent.children
          .map((c) => c.id)
          .filter((id) => id !== childId);
        let updatedArray = prevMenuArray.filter(
          (id) => !otherChildIds.includes(id)
        );

        if (checked && !updatedArray.includes(childId)) {
          updatedArray.push(childId);
        } else if (!checked) {
          updatedArray = updatedArray.filter((id) => id !== childId);
        }

        const hasCheckedChild = parent.children.some((c) =>
          updatedArray.includes(c.id)
        );
        if (hasCheckedChild && !updatedArray.includes(parentId)) {
          updatedArray.push(parentId);
        } else if (!hasCheckedChild) {
          updatedArray = updatedArray.filter((id) => id !== parentId);
        }

        return updatedArray;
      }

      if (checked) {
        return prevMenuArray.includes(childId)
          ? prevMenuArray
          : [...prevMenuArray, childId];
      } else {
        return prevMenuArray.filter((id) => id !== childId);
      }
    });
  };

  // Updates selection of all children under a specific parent without affecting other parents
  const handleSelectAll = (parentId, checked) => {
    const parentItem = adminMenuPermissions.find(
      (item) => item.id === parentId
    );

    if (parentItem && parentItem.children) {
      const childIds = parentItem.children.map((child) => child.id);

      setMenuArray((prevMenuArray) => {
        if (checked) {
          return [...new Set([...prevMenuArray, parentId, ...childIds])];
        } else {
          return prevMenuArray.filter(
            (id) => id !== parentId && !childIds.includes(id)
          );
        }
      });
    }
  };

  const handlePermissionTypeChange = (event) => {
    const { value } = event.target;
    setPermissionType(value === "true");
  };

  const handleOrderChange = (e) => {
    const value = e.target.value;

    if (value.startsWith("0")) {
      return;
    }

    if (value.length > 0) {
      setErrOrderMessage("");
    }

    if (value.length <= 6) {
      setOrderNumber(value);
    }
  };

  const handleRoleNameChange = (event) => {
    const value = event.target.value;
    const isValidName = ALPHABETIC_REGEX.test(value);

    if (isValidName) {
      setRoleName(value);
      setRoleNameError("");
    } else {
      setRoleNameError(roleMange.errorMsg.rolenameErrorSet);
    }
  };

  const handleTfaChange = (event) => {
    const { value } = event.target;
    const isSelected = value !== "false";
    setIsTwoFactorEnabled(isSelected);
  };

  const handleActiveChange = (event) => {
    const { value } = event.target;
    const isActiveSelected = value !== "false";
    setIsActive(isActiveSelected);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let isValid = true;

    if (!roleName) {
      setRoleNameError(roleMange.errorMsg.roleNameError);
      refsMap.name.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      isValid = false;
    }
    if (!orderNumber) {
      setErrOrderMessage(roleMange.errorMsg.errorOrderMsg);
      refsMap.name.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    try {
      const navItemIds = menuArray
        .filter(
          (id) => id !== undefined && id !== null && !isNaN(parseInt(id, 10))
        )
        .map((id) => parseInt(id, 10));
      const response = await privateAxios.put(
        getUserManagementWithPermissionsURL(id),
        {
          name: roleName,
          fullAccess: permissionType,
          orderNumber: orderNumber,
          isTwoFactorEnabled: isTwoFactorEnabled,
          isActive: isActive,
          navItemIds: navItemIds
        }
      );
      showToast(response.data.message, showToastType.success);
      setRoleName("");
      setPermissionType(true);
      setOrderNumber("");
      setIsTwoFactorEnabled(false);
      navigate(ROLE);
    } catch (error) {
      if (error.response) {
        showToast(error.response.data.message, showToastType.error);
      }
    }
  };

  const handleCancel = () => {
    setRoleName("");
    setPermissionType(true);
    setOrderNumber("");
    setIsTwoFactorEnabled(false);
    navigate(ROLE);
  };

  return (
    <>
      <TablePagesTitle
        mainTitle={userManage.mainTitle}
        content={roleMange.content}
      />
      <AccessPageAction
        subheading={roleMange.editRole}
        alluser={ROLE}
        allUserTitle={roleMange.subheading}
        createuser={CREATE_ROLE}
        createUserTitle={roleMange.createRole}
      />
      <div className="row mb-3 mx-0">
        <div className="col-md-12 sale-leade-form mt-3">
          <form onSubmit={handleSubmit}>
            <div className="row mb-2">
              <div className="col-sm-4 col-xl-2">
                <div className="input-title">
                  <label>{roleMange.label1}</label>
                </div>
              </div>
              <div className="col-sm-8 col-xl-10">
                <div className="input-group">
                  <input
                    ref={roleNameRef}
                    type="text"
                    placeholder={roleMange.placeholder1}
                    maxLength={30}
                    name="name"
                    onChange={handleRoleNameChange}
                    value={roleName}
                  />
                  {roleNameError && (
                    <div className="text-danger small">{roleNameError}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-sm-4 col-xl-2">
                <div className="input-title">
                  <label>{roleMange.label2}</label>
                </div>
              </div>
              <div className="col-sm-8 col-xl-10">
                <div className="input-group">
                  <select
                    name="isTwoFactorEnabled"
                    id="isTwoFactorEnabled"
                    onChange={handleTfaChange}
                    value={String(isTwoFactorEnabled)}
                  >
                    {getTwoFactorOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-sm-4 col-xl-2">
                <div className="input-title">
                  <label>{roleMange.label3}</label>
                </div>
              </div>
              <div className="col-sm-8 col-xl-10">
                <div className="input-group">
                  <input
                    ref={orderNumberRef}
                    type="number"
                    min={1}
                    name="orderNumber"
                    onChange={handleOrderChange}
                    value={orderNumber}
                  />
                  {errOrderMessage && (
                    <div className="text-danger small">{errOrderMessage}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4 col-xl-2">
                <div className="input-title">
                  <label>{roleMange.label4}</label>
                </div>
              </div>
              <div className="col-sm-8 col-xl-10">
                <div className="input-group">
                  <select
                    name="isActive"
                    id="isActive"
                    onChange={handleActiveChange}
                    value={String(isActive)}
                  >
                    {getActiveStatusOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4 col-xl-2">
                <div className="input-title">
                  <label>{roleMange.label5}</label>
                </div>
              </div>
              <div className="col-sm-8 col-xl-10">
                <div className="input-group">
                  <select
                    ref={permissionAccessRef}
                    name="fullAccess"
                    id="fullAccess"
                    onChange={handlePermissionTypeChange}
                    value={String(permissionType)}
                  >
                    {getPermissionAccessOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {permissionType === false && (
              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{roleMange.label6}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="border p-3">
                    {adminMenuPermissions?.map((item) => (
                      <div key={item.id} className="mb-3 permission-group">
                        <div className="mb-2">
                          <div className="input-title">
                            <label
                              className={
                                isRadioGroupNavItem(item.slugName)
                                  ? ""
                                  : "cursor-pointer"
                              }
                            >
                              {!isRadioGroupNavItem(item.slugName) && (
                                <input
                                  type="checkbox"
                                  className="me-1 select-all"
                                  checked={
                                    menuArray.includes(item.id) &&
                                    item.children.every((child) =>
                                      menuArray.includes(child.id)
                                    )
                                  }
                                  onChange={(e) =>
                                    handleSelectAll(item.id, e.target.checked)
                                  }
                                />
                              )}
                              {item.name}
                            </label>
                          </div>
                        </div>
                        {item.children && item.children.length > 0 && (
                          <div className="row permissions-list">
                            {item.children.map((child) => (
                              <div className="col-lg-6 col-xl-4" key={child.id}>
                                <input
                                  type={
                                    isRadioGroupNavItem(item.slugName)
                                      ? "radio"
                                      : "checkbox"
                                  }
                                  id={`menu-${child.id}`}
                                  name={
                                    isRadioGroupNavItem(item.slugName)
                                      ? `menu-group-${item.id}`
                                      : undefined
                                  }
                                  checked={menuArray.includes(child.id)}
                                  onChange={(e) =>
                                    handleChildChange(
                                      item.id,
                                      child.id,
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  htmlFor={`menu-${child.id}`}
                                  className="ms-1 cursor-pointer"
                                >
                                  {child.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="row mt-4">
              <div className="col-sm-8 offset-sm-2 d-flex d-sm-block justify-content-center justify-content-sm-start">
                <button
                  type="button"
                  className="btn btn-red me-2"
                  onClick={handleCancel}
                >
                  {buttonName.cancel}
                </button>
                <button type="submit" className="btn btn-primary">
                  {buttonName.update}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditRoleManage;
